import React, { useState } from "react"
import {
  Card,
  Popover,
  Alert,
  theme as themeConfig,
  Divider,
  Spin,
  Badge,
  Tooltip,
} from "antd"
import {
  CommentOutlined,
  FieldTimeOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  DisconnectOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
  LinkOutlined,
  FolderOpenOutlined,
  ProfileOutlined,
  RiseOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  SoundOutlined,
  ClockCircleOutlined,
  UserOutlined,
  AimOutlined,
  TrophyOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import ErrorBoundary from "../../../../../../components/ErrorBoundary"

import RefreshFilters from "../../../Metrics/RefreshFilters"

import "./index.scss"

const MetricItem = ({ metric = {} }) => {
  const { token } = themeConfig.useToken()
  const metricStateClass =
    metric?.attributes?.state &&
    metric?.attributes?.state?.toLowerCase() !== "normal"
      ? `metrics-value-${metric?.attributes?.state?.toLowerCase()}`
      : ""

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 80,
      }}
    >
      <p
        className={metricStateClass}
        style={{
          fontWeight: token.fontWeightStrong,
          fontSize: token.fontSizeHeading3,
          margin: 0,
          display: "flex",
          alignItems: "baseline",
        }}
      >
        {metric?.prefix && (
          <span
            style={{
              fontSize: token.fontSizeSM,
              marginRight: 2,
            }}
          >
            {metric.prefix}
          </span>
        )}
        {metric?.value}
        {metric?.suffix && (
          <span
            style={{
              fontSize: token.fontSizeSM,
              marginLeft: 2,
            }}
          >
            {metric.suffix}
          </span>
        )}
      </p>

      {/* Title */}
      <p
        style={{
          fontSize: token.fontSizeSM,
          color: metricStateClass ? "" : token.colorTextDescription,
          margin: 0,
        }}
        className={metricStateClass}
      >
        {metric?.title}
      </p>

      {/* Previous Details */}
      {metric?.prevDetails && (
        <div
          style={{
            fontSize: token.fontSizeXS,
            display: "flex",
            alignItems: "center",
            marginTop: 4,
            color:
              metric.prevDetails.sentiment === "positive"
                ? token.colorSuccess
                : metric.prevDetails.sentiment === "negative"
                ? token.colorError
                : token.colorTextSecondary,
          }}
        >
          <span style={{ marginRight: 4 }}>
            {metric?.prevDetails?.trend === "up"
              ? "↑"
              : metric?.prevDetails?.trend === "down"
              ? "↓"
              : "→"}
          </span>
          <span style={{ marginRight: 4 }}>{metric?.prevDetails?.value}</span>
          <span>({metric?.prevDetails?.percentChange})</span>
        </div>
      )}
    </div>
  )
}

const MetricsCard = ({ item, prevDetails, loadError, loading, onRefresh }) => {
  const { token } = themeConfig.useToken()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const getStateClass = state => {
    switch (state?.toLowerCase()) {
      case "success":
        return "metrics-card-success"
      case "warning":
        return "metrics-card-warning"
      case "error":
        return "metrics-card-error"
      case "normal":
      default:
        return "metrics-card-normal"
    }
  }

  const getIconBasedOnStatType = icon => {
    const icons = {
      message: <CommentOutlined />,
      time: <FieldTimeOutlined />,
      users: <TeamOutlined />,
      done: <CheckCircleOutlined />,
      disconnect: <DisconnectOutlined />,
      assign: <UserAddOutlined />,
      transfer: <UserSwitchOutlined />,
      session: <LinkOutlined />,
      open: <FolderOpenOutlined />,
      total: <ProfileOutlined />,
      progress: <RiseOutlined />,
      call: <PhoneOutlined />,
      speed: <ThunderboltOutlined />,
      voicemail: <SoundOutlined />,
      timeout: <ClockCircleOutlined />,
      engagement: <UserOutlined />,
      success: <TrophyOutlined />,
    }
    return icons[icon] || null
  }

  const handleRefresh = refreshData => {
    const dataWithId = {
      ...refreshData,
      id: item?.id,
    }

    if (onRefresh) {
      onRefresh(dataWithId)
    }
  }

  return (
    <>
      <Card
        size="small"
        className={`ori-animated ori-fade-in ${getStateClass(
          item?.attributes?.state
        )}`}
        style={{
          minHeight: prevDetails ? 170 : 147,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadowTertiary,
          position: "relative",
        }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {loadError ? (
                <ExclamationCircleOutlined
                  style={{ color: token.colorError }}
                />
              ) : loading ? (
                <Spin size="small" style={{ marginRight: token.marginXS }} />
              ) : (
                getIconBasedOnStatType(item?.icon)
              )}
              {item?.title || "Loading..."}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Refresh Button */}
              {!loadError && item?.refreshComponent && (
                <Tooltip title="Refresh Data">
                  <ReloadOutlined
                    style={{
                      color: token.colorTextDescription,
                      cursor: "pointer",
                      fontSize: token.fontSizeSM,
                    }}
                    onClick={() => setDrawerOpen(true)}
                  />
                </Tooltip>
              )}

              {/* Legend Popover */}
              {!loadError && !loading && item?.legend && (
                <Popover
                  trigger="hover"
                  placement="bottomRight"
                  styles={{
                    body: { padding: 0, minWidth: 520 },
                    root: { maxWidth: 600 },
                  }}
                  content={
                    <Alert
                      type="info"
                      showIcon
                      style={{
                        padding: token.paddingXS,
                        fontSize: token.fontSize,
                      }}
                      message={
                        <div
                          style={{
                            fontSize: token.fontSize,
                            fontWeight: token.fontWeightStrong,
                          }}
                        >
                          {item.legend.title}
                        </div>
                      }
                      description={
                        <div
                          style={{
                            fontSize: token.fontSizeSM,
                            marginTop: token.marginXS,
                            maxHeight: 300,
                            overflow: "auto",
                            minHeight: 120,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {item?.legend?.data?.map((criteria, index) => (
                              <div
                                key={index}
                                style={{
                                  flex: "1 1 200px",
                                  padding: token.paddingXS,
                                  border: `1px solid ${token.colorBorderSecondary}`,
                                  borderRadius: token.borderRadiusLG,
                                  background: token.colorBgElevated,
                                }}
                              >
                                {criteria?.subtitle && (
                                  <div
                                    style={{
                                      fontWeight: token.fontWeightStrong,
                                      marginBottom: token.marginXXS,
                                      fontSize: token.fontSizeSM,
                                    }}
                                  >
                                    {criteria.subtitle}
                                  </div>
                                )}
                                {criteria?.successCriteria && (
                                  <div
                                    style={{
                                      marginBottom: token.marginXXS,
                                      fontSize: token.fontSizeSM,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: token.fontWeightStrong,
                                      }}
                                    >
                                      Success Criteria:
                                    </span>{" "}
                                    {criteria.successCriteria}
                                  </div>
                                )}
                                {criteria?.threshold && (
                                  <div style={{ marginTop: token.marginXXS }}>
                                    <div
                                      style={{
                                        fontWeight: token.fontWeightStrong,
                                        marginBottom: token.marginXXS,
                                        fontSize: token.fontSizeSM,
                                      }}
                                    >
                                      Thresholds:
                                    </div>
                                    <ul
                                      style={{
                                        margin: 0,
                                        paddingLeft: 0,
                                        listStyleType: "none",
                                      }}
                                    >
                                      {criteria?.threshold?.success && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#52c41a",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            {criteria.threshold.success}
                                          </span>
                                        </li>
                                      )}
                                      {criteria?.threshold?.warn && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#faad14",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>{criteria.threshold.warn}</span>
                                        </li>
                                      )}
                                      {criteria?.threshold?.error && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#ff4d4f",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            {criteria.threshold.error}
                                          </span>
                                        </li>
                                      )}
                                      {criteria?.threshold?.normal && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#d9d9d9",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            Normal: {criteria.threshold.normal}
                                          </span>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      }
                    />
                  }
                >
                  <AimOutlined
                    style={{
                      color: token.colorTextDescription,
                      cursor: "pointer",
                      fontSize: token.fontSizeSM,
                    }}
                  />
                </Popover>
              )}

              {/* Existing Tooltip */}
              {!loadError &&
                !loading &&
                item?.tooltip?.data?.some(d => d?.description) && (
                  <Popover
                    trigger="hover"
                    placement="bottomRight"
                    styles={{
                      body: { padding: 0, minWidth: 200 },
                      root: { maxWidth: 280 },
                    }}
                    content={
                      <Alert
                        type="info"
                        showIcon
                        style={{
                          padding: token.paddingXS,
                          fontSize: token.fontSize,
                        }}
                        message={
                          <div
                            style={{
                              fontSize: token.fontSize,
                              fontWeight: token.fontWeightStrong,
                            }}
                          >
                            {item?.tooltip?.title}
                          </div>
                        }
                        description={
                          <div
                            style={{
                              fontSize: token.fontSizeSM,
                              marginTop: token.marginXS,
                            }}
                          >
                            {item?.tooltip?.data?.map((tip, index) =>
                              tip?.description ? (
                                <div
                                  key={index}
                                  style={{ marginBottom: 2, lineHeight: 1.3 }}
                                >
                                  {tip?.subtitle ? (
                                    <strong>{tip.subtitle}</strong>
                                  ) : null}
                                  {tip?.subtitle && tip?.description
                                    ? ": "
                                    : ""}
                                  {tip?.description}
                                </div>
                              ) : null
                            )}
                          </div>
                        }
                      />
                    }
                  >
                    <InfoCircleOutlined
                      style={{
                        color: token.colorTextDescription,
                        cursor: "pointer",
                        fontSize: token.fontSizeSM,
                      }}
                    />
                  </Popover>
                )}
            </div>
          </div>
        }
      >
        {item?.updated && (
          <Badge.Ribbon
            text="Updated"
            size="small"
            style={{
              position: "absolute",
              top: "-63px",
              right: "-19px",
              backgroundColor: token.colorPrimary,
              color: "white",
              fontSize: "10px",
            }}
          />
        )}
        {loadError ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: prevDetails ? 100 : 80,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: token.fontSizeHeading4,
                fontWeight: token.fontWeightStrong,
                marginBottom: token.marginXS,
              }}
            >
              Error
            </div>
            <div
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextDescription,
              }}
            >
              Unable to fetch
            </div>
          </div>
        ) : loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: prevDetails ? 100 : 80,
            }}
          >
            <Spin />
          </div>
        ) : (
          <div
            style={{
              position: "relative",
            }}
          >
            {/* Metrics */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
                maxWidth: "100%",
                flexWrap: "wrap",
              }}
              className={getStateClass(item?.attributes?.state)}
            >
              {item?.metricsData?.map((metric, i) => (
                <ErrorBoundary key={i}>
                  <MetricItem metric={metric} />
                </ErrorBoundary>
              ))}
            </div>

            {/* More Metrics */}
            {item?.hasMoreMetrics && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: token.marginXS,
                }}
                className={getStateClass(item?.attributes?.state)}
              >
                <Popover
                  trigger="hover"
                  placement="bottomRight"
                  styles={{
                    body: { padding: token.paddingXS, minWidth: 220 },
                    root: { maxWidth: 300 },
                  }}
                  content={
                    <div
                      style={{
                        padding: token.paddingXXS,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: token.fontWeightStrong,
                          fontSize: token.fontSize,
                          marginBottom: token.marginXS,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        More Metrics
                      </div>
                      <Divider
                        style={{
                          margin: 0,
                          marginBottom: token.marginXXS,
                          width: "100%",
                          alignSelf: "stretch",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-evenly",
                          gap: token.marginXS,
                          marginTop: token.marginSM,
                        }}
                      >
                        {item?.moreMetricsData.map((metric, i) => (
                          <ErrorBoundary key={i}>
                            <MetricItem metric={metric} />
                          </ErrorBoundary>
                        ))}
                      </div>
                    </div>
                  }
                >
                  <span
                    style={{
                      fontSize: token.fontSizeSM,
                      color: token.colorInfo,
                      cursor: "pointer",
                    }}
                  >
                    Show more
                  </span>
                </Popover>
              </div>
            )}

            {drawerOpen && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.25)",
                  zIndex: 1000,
                  cursor: "pointer",
                }}
                onClick={() => setDrawerOpen(false)}
              />
            )}
            {drawerOpen && (
              <RefreshFilters
                open={drawerOpen}
                item={item}
                onClose={() => setDrawerOpen(false)}
                onRefresh={handleRefresh}
                loading={loading}
                styles={{
                  top: -11,
                  right: -11,
                  width: "300px",
                }}
              />
            )}
          </div>
        )}
      </Card>
    </>
  )
}

export default MetricsCard
